import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useEditConfirm } from "../../context/EditConfirmContext";
import { formatET } from "../../lib/dateUtils";
import { Switch } from "../ui/switch";

const TOGGLEABLE_MODULES = [
  { key: "trust", label: "Trust & Asset Protection" },
  { key: "funding", label: "Business Funding" },
  { key: "affiliate", label: "Sales Reps" },
  { key: "funding_pipeline", label: "Funding Pipeline (SAB001-style)" },
];

export function StoreSettingsModal({ store, open, onOpenChange, onSaved }) {
  const confirmEdit = useEditConfirm();
  const [form, setForm] = useState({
    estimated_months_current_rate: store?.estimated_months_current_rate ?? "",
    estimated_months_full_ramp: store?.estimated_months_full_ramp ?? "",
    current_month_revenue: store?.current_month_revenue ?? "",
    current_month_label: store?.current_month_label ?? "",
  });
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [unlockedModules, setUnlockedModules] = useState([]);
  const [achEnabled, setAchEnabled] = useState(true);

  useEffect(() => {
    if (store) {
      setForm({
        estimated_months_current_rate: store.estimated_months_current_rate ?? "",
        estimated_months_full_ramp: store.estimated_months_full_ramp ?? "",
        current_month_revenue: store.current_month_revenue ?? "",
        current_month_label: store.current_month_label ?? "",
      });
      setUnlockedModules(store.unlocked_modules ?? []);
      setAchEnabled(store.ach_enabled ?? true);
      fetchNotes();
    }
  }, [store]);

  const toggleAch = async (enabled) => {
    if (!(await confirmEdit(`${store.display_name}'s ACH payment status`))) return;
    try {
      await api.patch(`/admin/stores/${store.client_id}`, { ach_enabled: enabled });
      setAchEnabled(enabled);
      toast.success(`ACH payments ${enabled ? "enabled" : "paused"} for ${store.display_name}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleModule = async (moduleKey, enabled) => {
    const label = TOGGLEABLE_MODULES.find((m) => m.key === moduleKey)?.label || moduleKey;
    if (!(await confirmEdit(`${store.display_name}'s access to ${label}`))) return;
    try {
      await api.patch(`/admin/stores/${store.client_id}/module-access`, { module_key: moduleKey, enabled });
      setUnlockedModules((prev) => (enabled ? [...prev, moduleKey] : prev.filter((k) => k !== moduleKey)));
      toast.success(`${label} ${enabled ? "unlocked" : "locked"} for ${store.display_name}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fetchNotes = async () => {
    if (!store) return;
    const { data } = await api.get(`/admin/stores/${store.client_id}/notes`);
    setNotes(data);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/stores/${store.client_id}/notes`, { text: newNote });
      setNewNote("");
      fetchNotes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async () => {
    if (!(await confirmEdit(`${store.display_name}'s store settings`))) return;
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null) payload[k] = ["current_month_label"].includes(k) ? v : parseFloat(v);
      });
      await api.patch(`/admin/stores/${store.client_id}`, payload);
      toast.success(`${store.display_name} settings updated`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!store) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream max-w-md" data-testid="store-settings-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-igc-cream" title="Controls what this client can see and do in their portal, plus internal admin-only notes.">{store.display_name} — Store Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Current Month Label (e.g. Aug)</label>
            <Input data-testid="store-settings-month-label-input" title="A short label for the current reporting month, shown to the client on their dashboard (e.g. 'Aug')." value={form.current_month_label} onChange={(e) => setForm({ ...form, current_month_label: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Current Month Revenue ($, in progress)</label>
            <Input data-testid="store-settings-month-revenue-input" type="number" title="The store's revenue so far this month, shown to the client while the month is still in progress." value={form.current_month_revenue} onChange={(e) => setForm({ ...form, current_month_revenue: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Est. Months to Recovery (current rate)</label>
            <Input data-testid="store-settings-months-current-input" type="number" title="If the store keeps growing at its current rate, how many months until the client fully recovers their upfront investment." value={form.estimated_months_current_rate} onChange={(e) => setForm({ ...form, estimated_months_current_rate: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block">Est. Months to Recovery (full ramp)</label>
            <Input data-testid="store-settings-months-ramp-input" type="number" title="If the store reaches its full, fully-optimized sales rate, how many months until the client fully recovers their upfront investment." value={form.estimated_months_full_ramp} onChange={(e) => setForm({ ...form, estimated_months_full_ramp: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
          </div>
          <Button data-testid="store-settings-save-button" onClick={save} title="Saves all the revenue and projection fields above for this client's dashboard." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
            Save Settings
          </Button>

          <div className="border-t border-igc-gold/10 pt-4" data-testid="module-access-section">
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-3 block" title="Controls which sections of the portal this client is allowed to see and use.">Module Access Control</label>
            <div className="space-y-3">
              {TOGGLEABLE_MODULES.map((m) => (
                <div key={m.key} className="flex items-center justify-between">
                  <span className="text-igc-cream text-sm">{m.label}</span>
                  <Switch
                    data-testid={`module-toggle-${m.key}`}
                    title={`Turns the ${m.label} section on or off for this client. When off, they can't see or access it at all.`}
                    checked={unlockedModules.includes(m.key)}
                    onCheckedChange={(checked) => toggleModule(m.key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-igc-gold/10 pt-4" data-testid="ach-status-section">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-igc-cream text-sm">ACH Payment Status</p>
                <p className="text-igc-muted text-xs">{achEnabled ? "Active — payments processing normally" : "Paused — ACH payments on hold"}</p>
              </div>
              <Switch data-testid="ach-status-toggle" title="Turns this client's ACH bank payments on or off. Pausing stops new charges from processing without deleting any payment history." checked={achEnabled} onCheckedChange={toggleAch} />
            </div>
          </div>

          <div className="border-t border-igc-gold/10 pt-4">
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block" title="Private notes about this store, visible only to admins — never shown to the client.">Internal Notes Log (admin-only)</label>
            <div className="flex gap-2 mb-3">
              <Textarea data-testid="store-note-input" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note about this store..." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-16" />
            </div>
            <Button data-testid="store-note-add-button" onClick={addNote} title="Saves this note to the internal notes log below." className="w-full bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black mb-3">
              Add Note
            </Button>
            <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="store-notes-list">
              {notes.length === 0 && <p className="text-igc-muted text-xs">No notes yet.</p>}
              {notes.map((n) => (
                <div key={n.id} className="border-l-2 border-igc-gold pl-3 py-1" data-testid={`store-note-${n.id}`}>
                  <p className="text-igc-cream text-sm">{n.text}</p>
                  <p className="text-igc-muted text-[11px]">{n.created_by} — {formatET(n.timestamp, "MMM d, yyyy h:mm a")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
